import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fmtCLP } from "./formato";
import { shouldUseRawBT } from "./printDriver";

const W = 80;
const M = 6;
const H_PAGINA = 210;
const MARGEN_PIE = 15;

function nuevaPaginaSiCierre(doc, y) {
  if (y > H_PAGINA - MARGEN_PIE) {
    doc.addPage([W, H_PAGINA], "p");
    return 15;
  }
  return y;
}

const DENOM_LABELS = {
  20000: "$20.000",
  10000: "$10.000",
  5000: "$5.000",
  2000: "$2.000",
  1000: "$1.000",
  500: "$500",
  100: "$100",
  50: "$50",
  10: "$10",
};

function fechaHora(d) {
  return new Date(d).toLocaleString("es-CL", {
    dateStyle: "short",
    timeStyle: "medium",
    timeZone: "America/Santiago",
  });
}

// ─── ESC/POS helpers ─────────────────────────────────────────────────
const _ESC = '\x1b';
const _GS  = '\x1d';
const _EP = {
  INIT:     _ESC + '@',
  CENTER:   _ESC + '\x61\x01',
  LEFT:     _ESC + '\x61\x00',
  BOLD_ON:  _ESC + '\x45\x01',
  BOLD_OFF: _ESC + '\x45\x00',
  DBL:      _GS  + '\x21\x11',
  NORMAL:   _GS  + '\x21\x00',
  FEED3:    _ESC + '\x64\x03',
  CUT:      _ESC + '\x69',
};

function _fmt(n) {
  return '$' + (Number(n) || 0).toLocaleString('es-CL');
}
function _lr(left, right, w = 48) {
  return left + ' '.repeat(Math.max(1, w - left.length - right.length)) + right;
}
function _dispatchRawBT(escpos) {
  const a = document.createElement('a');
  a.setAttribute('href', 'rawbt:' + encodeURIComponent(escpos));
  a.style.cssText = 'display:none;position:absolute;';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => a.parentNode?.removeChild(a), 500);
}

function buildCierreEscPos({ nombreCajero = '', cierre = {}, resumen = {}, desgloseEfectivo = null, movimientos = [], apertura = null }) {
  const W = 48;
  const SEP = '-'.repeat(W);
  const N = (v) => Number(v || 0);

  const totalTarjeta = resumen.total_tarjeta != null ? N(resumen.total_tarjeta) : N(resumen.total_debito) + N(resumen.total_credito);
  const tieneApertura = apertura && apertura.monto_total != null;
  const fondoInicial = apertura?.monto_total != null ? N(apertura.monto_total) : 0;
  const totalIngresos = (movimientos || []).filter(m => m.tipo === 'ingreso').reduce((s, m) => s + N(m.monto), 0);
  const totalEgresos  = (movimientos || []).filter(m => m.tipo === 'egreso').reduce((s, m) => s + N(m.monto), 0);
  const efectivoEsperado = cierre.efectivo_esperado != null
    ? N(cierre.efectivo_esperado)
    : fondoInicial + N(resumen.total_efectivo) + totalIngresos - totalEgresos;
  const tieneConteo = cierre.efectivo_contado != null;
  const efectivoContado = tieneConteo ? N(cierre.efectivo_contado) : 0;
  const diferenciaEfectivo = cierre.diferencia_efectivo != null
    ? N(cierre.diferencia_efectivo)
    : efectivoContado - efectivoEsperado;

  const fh = (d) => d ? new Date(d).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Santiago' }) : '';

  let t = _EP.INIT;

  // Encabezado
  t += _EP.CENTER;
  t += _EP.BOLD_ON + 'La Sushitería\n' + _EP.BOLD_OFF;
  t += 'CIERRE DE CAJA\n';
  t += fh(cierre.hasta) + '\n';

  // Datos del turno
  t += _EP.LEFT + SEP + '\n';
  t += `Cajero    ${nombreCajero}\n`;
  t += `Desde     ${fh(cierre.desde)}\n`;
  t += `Hasta     ${fh(cierre.hasta)}\n`;

  // Fondo inicial
  if (tieneApertura) {
    t += SEP + '\n';
    t += _lr('Fondo inicial', _fmt(fondoInicial)) + '\n';
  }

  // Ventas
  t += SEP + '\n';
  t += _EP.BOLD_ON + 'VENTAS\n' + _EP.BOLD_OFF;
  t += _lr('Total pedidos', String(N(resumen.total_pedidos))) + '\n';
  t += _lr('Ventas netas',  _fmt(resumen.total_neto)) + '\n';
  t += _lr('Efectivo',      _fmt(resumen.total_efectivo)) + '\n';
  t += _lr('Transferencia', _fmt(resumen.total_transfer)) + '\n';
  t += _lr('Tarjeta',       _fmt(totalTarjeta)) + '\n';
  if (N(resumen.total_otro) > 0) t += _lr('Otro', _fmt(resumen.total_otro)) + '\n';

  // Movimientos
  if ((movimientos || []).length > 0) {
    t += SEP + '\n';
    t += _EP.BOLD_ON + 'MOVIMIENTOS\n' + _EP.BOLD_OFF;
    for (const m of movimientos) {
      const hora = new Date(m.creado_en).toLocaleString('es-CL', { timeStyle: 'short', timeZone: 'America/Santiago' });
      const signo = m.tipo === 'ingreso' ? '+' : '-';
      const concepto = (m.concepto || '').substring(0, 20);
      t += _lr(`${hora} ${signo} ${concepto}`, signo + _fmt(m.monto)) + '\n';
    }
    t += _lr('Total ingresos', _fmt(totalIngresos)) + '\n';
    t += _lr('Total egresos',  _fmt(totalEgresos))  + '\n';
  }

  // Cuadre efectivo
  if (tieneApertura || (movimientos || []).length > 0) {
    t += SEP + '\n';
    t += _EP.BOLD_ON + 'CUADRE EFECTIVO\n' + _EP.BOLD_OFF;
    t += _lr('Fondo inicial',    _fmt(fondoInicial))               + '\n';
    t += _lr('+ Efectivo ventas',_fmt(resumen.total_efectivo))     + '\n';
    t += _lr('+ Ingresos',       _fmt(totalIngresos))              + '\n';
    t += _lr('- Egresos',        _fmt(totalEgresos))               + '\n';
    t += _lr('= Efectivo esperado', _fmt(efectivoEsperado))        + '\n';
    if (tieneConteo) {
      t += _lr('Contado',        _fmt(efectivoContado))            + '\n';
      t += _lr(diferenciaEfectivo >= 0 ? 'Sobra' : 'Falta', _fmt(Math.abs(diferenciaEfectivo))) + '\n';
    }
  }

  // Desglose efectivo
  const orden = [20000, 10000, 5000, 2000, 1000, 500, 100, 50, 10];
  if (desgloseEfectivo && Object.keys(desgloseEfectivo).length > 0) {
    t += SEP + '\n';
    t += _EP.BOLD_ON + 'DESGLOSE EN CAJA\n' + _EP.BOLD_OFF;
    let totalContado = 0;
    for (const v of orden) {
      if ((desgloseEfectivo[v] || 0) > 0) {
        const cant = desgloseEfectivo[v];
        totalContado += cant * v;
        t += _lr(`${_fmt(v)}  x${cant}`, _fmt(cant * v)) + '\n';
      }
    }
    t += _lr('Total contado', _fmt(totalContado)) + '\n';
  }

  // Totales destacados del cierre.
  const totalVentasStr = _fmt(resumen.total_neto);
  const efectivoEsperadoStr = _fmt(efectivoEsperado);
  const ventasLabel = 'VENTAS NETAS';
  const efectivoLabel = 'EFECTIVO ESP.';
  const spVentas = Math.max(1, 24 - ventasLabel.length - totalVentasStr.length);
  const spEfectivo = Math.max(1, 24 - efectivoLabel.length - efectivoEsperadoStr.length);
  t += SEP + '\n';
  t += _EP.BOLD_ON + _EP.DBL + ventasLabel + ' '.repeat(spVentas) + totalVentasStr + '\n';
  t += efectivoLabel + ' '.repeat(spEfectivo) + efectivoEsperadoStr + '\n' + _EP.NORMAL + _EP.BOLD_OFF;

  t += _EP.CENTER + '\n-- Fin del ticket --\n';
  t += _EP.FEED3 + _EP.CUT;

  return t;
}

function buildAperturaEscPos({ nombreCajero = '', monto_total = 0, desglose = null, fecha = new Date() }) {
  const W = 48;
  const SEP = '-'.repeat(W);
  const fh = (d) => new Date(d).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Santiago' });

  let t = _EP.INIT;

  t += _EP.CENTER;
  t += _EP.BOLD_ON + 'La Sushitería\n' + _EP.BOLD_OFF;
  t += 'APERTURA DE CAJA\n';
  t += fh(fecha) + '\n';

  t += _EP.LEFT + SEP + '\n';
  t += `Cajero    ${nombreCajero}\n`;
  t += `Fecha     ${fh(fecha)}\n`;
  t += 'Operación Inicio de turno\n';

  const orden = [20000, 10000, 5000, 2000, 1000, 500, 100, 50, 10];
  if (desglose && Object.keys(desglose).length > 0) {
    t += SEP + '\n';
    t += _EP.BOLD_ON + 'DESGLOSE\n' + _EP.BOLD_OFF;
    for (const v of orden) {
      if ((desglose[v] || 0) > 0) {
        t += _lr(`${_fmt(v)}  x${desglose[v]}`, _fmt(desglose[v] * v)) + '\n';
      }
    }
  }

  const totalStr = _fmt(monto_total);
  const sp = Math.max(1, 24 - 'FONDO DE CAJA'.length - totalStr.length);
  t += SEP + '\n';
  t += _EP.BOLD_ON + _EP.DBL + 'FONDO DE CAJA' + ' '.repeat(sp) + totalStr + '\n' + _EP.NORMAL + _EP.BOLD_OFF;

  t += _EP.CENTER + '\n-- Fin del ticket --\n';
  t += _EP.FEED3 + _EP.CUT;
  return t;
}

function buildMovimientoEscPos({ nombreCajero = '', tipo = 'ingreso', monto = 0, concepto = '', fecha = new Date() }) {
  const W = 48;
  const SEP = '-'.repeat(W);
  const fh = (d) => new Date(d).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Santiago' });
  const esIngreso = tipo === 'ingreso';

  let t = _EP.INIT;

  t += _EP.CENTER;
  t += _EP.BOLD_ON + 'La Sushitería\n' + _EP.BOLD_OFF;
  t += 'MOVIMIENTO DE CAJA\n';
  t += _EP.BOLD_ON + (esIngreso ? 'INGRESO' : 'EGRESO') + '\n' + _EP.BOLD_OFF;

  t += _EP.LEFT + SEP + '\n';
  t += `Cajero    ${nombreCajero}\n`;
  t += `Fecha     ${fh(fecha)}\n`;
  t += `Concepto  ${(concepto || '—').substring(0, 35)}\n`;

  const signo = esIngreso ? '+' : '-';
  const montoStr = signo + ' ' + _fmt(monto);
  const sp = Math.max(1, 24 - montoStr.length);
  t += SEP + '\n';
  t += _EP.BOLD_ON + _EP.DBL + ' '.repeat(sp) + montoStr + '\n' + _EP.NORMAL + _EP.BOLD_OFF;

  t += _EP.CENTER + '\n-- Fin del ticket --\n';
  t += _EP.FEED3 + _EP.CUT;
  return t;
}

/**
 * Genera y abre el diálogo de impresión del ticket de APERTURA de caja.
 * @param {{ nombreCajero: string, monto_total: number, desglose: Record<number, number> | null, fecha?: Date }} opts
 */
export function imprimirTicketApertura(opts) {
  if (shouldUseRawBT()) {
    _dispatchRawBT(buildAperturaEscPos(opts));
    return;
  }
  const { nombreCajero = "", monto_total = 0, desglose = null, fecha = new Date() } = opts;
  const doc = new jsPDF({ unit: "mm", format: [W, 210] });

  let y = 10;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("La Sushitería", W / 2, y, { align: "center" });
  y += 6;
  doc.setFontSize(12);
  doc.text("APERTURA DE CAJA", W / 2, y, { align: "center" });
  y += 10;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Cajero:", M, y);
  doc.text(String(nombreCajero), M + 22, y);
  y += 6;
  doc.text("Fecha/hora:", M, y);
  doc.text(fechaHora(fecha), M + 22, y);
  y += 6;
  doc.text("Operación:", M, y);
  doc.text("Inicio de turno - Fondo de caja", M + 22, y);
  y += 10;

  if (desglose && typeof desglose === "object" && Object.keys(desglose).length > 0) {
    const orden = [20000, 10000, 5000, 2000, 1000, 500, 100, 50, 10];
    const filas = orden
      .filter((v) => desglose[v] > 0)
      .map((v) => [
        DENOM_LABELS[v] || `$${v}`,
        String(desglose[v]),
        fmtCLP(v * desglose[v]),
      ]);
    if (filas.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Desglose", M, y);
      y += 5;
      doc.setFont("helvetica", "normal");
      autoTable(doc, {
        head: [["Denom.", "Cant.", "Subtotal"]],
        body: filas,
        startY: y,
        theme: "plain",
        margin: { left: M, right: M },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 28 },
          1: { cellWidth: 18, halign: "center" },
          2: { cellWidth: 24, halign: "right" },
        },
      });
      y = (doc.lastAutoTable?.finalY || y) + 4;
    }
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("FONDO DE CAJA (TOTAL):", M, y);
  doc.text(fmtCLP(monto_total), W - M, y, { align: "right" });
  y += 10;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("— Fin del ticket de apertura —", W / 2, y, { align: "center" });

  abrirEImprimir(doc);
}

function montoTicket(n) {
  return "$" + (Number(n) || 0).toLocaleString("es-CL", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/**
 * Genera y abre el diálogo de impresión del ticket de CIERRE de caja.
 * Incluye: inicio de caja, ventas, movimientos, cuadre efectivo, desglose.
 */
export function imprimirTicketCierre(opts) {
  if (shouldUseRawBT()) {
    _dispatchRawBT(buildCierreEscPos(opts));
    return;
  }
  const { nombreCajero = "", cierre = {}, resumen = {}, desgloseEfectivo = null, movimientos = [], apertura = null } = opts;
  const doc = new jsPDF({ unit: "mm", format: [W, H_PAGINA] });

  const N = (v) => Number(v || 0);
  const totalTarjeta = resumen.total_tarjeta != null ? N(resumen.total_tarjeta) : N(resumen.total_debito) + N(resumen.total_credito);
  const tieneApertura = apertura && apertura.monto_total != null;
  const fondoInicial = apertura && apertura.monto_total != null ? N(apertura.monto_total) : 0;
  const totalIngresosMov = Array.isArray(movimientos)
    ? movimientos.filter((m) => m.tipo === "ingreso").reduce((s, m) => s + N(m.monto), 0)
    : 0;
  const totalEgresosMov = Array.isArray(movimientos)
    ? movimientos.filter((m) => m.tipo === "egreso").reduce((s, m) => s + N(m.monto), 0)
    : 0;
  const efectivoVentas = N(resumen.total_efectivo);
  const efectivoEsperado = cierre.efectivo_esperado != null
    ? N(cierre.efectivo_esperado)
    : fondoInicial + efectivoVentas + totalIngresosMov - totalEgresosMov;
  const tieneConteo = cierre.efectivo_contado != null;
  const efectivoContado = tieneConteo ? N(cierre.efectivo_contado) : 0;
  const diferenciaEfectivo = cierre.diferencia_efectivo != null
    ? N(cierre.diferencia_efectivo)
    : efectivoContado - efectivoEsperado;

  let y = 10;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("La Sushitería", W / 2, y, { align: "center" });
  y += 6;
  doc.setFontSize(12);
  doc.text("CIERRE DE CAJA", W / 2, y, { align: "center" });
  y += 10;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Cajero:", M, y);
  doc.text(String(nombreCajero), M + 22, y);
  y += 6;
  doc.text("Operación:", M, y);
  doc.text("Cierre de turno", M + 22, y);
  y += 6;
  doc.text("Período:", M, y);
  doc.text(
    `${fechaHora(cierre.desde)} — ${fechaHora(cierre.hasta)}`,
    M + 22,
    y,
    { maxWidth: W - M - 24 }
  );
  y += 8;

  if (tieneApertura) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Inicio de caja (fondo inicial)", M, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.text(montoTicket(fondoInicial), W - M, y, { align: "right" });
    y += 6;
  }

  const filas = [
    ["Total pedidos", String(N(resumen.total_pedidos))],
    ["Ventas netas", montoTicket(resumen.total_neto)],
    ["Efectivo (ventas)", montoTicket(resumen.total_efectivo)],
    ["Transferencia", montoTicket(resumen.total_transfer)],
    ["Tarjeta", montoTicket(totalTarjeta)],
    ["Otro", montoTicket(resumen.total_otro)],
  ];

  autoTable(doc, {
    head: [["Concepto", "Monto"]],
    body: filas,
    startY: y,
    theme: "plain",
    margin: { left: M, right: M },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 48 },
      1: { cellWidth: 22, halign: "right" },
    },
  });
  y = (doc.lastAutoTable?.finalY || y) + 6;
  y = nuevaPaginaSiCierre(doc, y);

  if (Array.isArray(movimientos) && movimientos.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Movimientos del turno", M, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    const filasMov = movimientos.map((m) => [
      fechaHora(m.creado_en).slice(0, 12),
      m.tipo === "ingreso" ? "Ing" : "Eg",
      (m.concepto || "").slice(0, 18),
      (m.tipo === "ingreso" ? "+ " : "- ") + montoTicket(m.monto),
    ]);
    autoTable(doc, {
      head: [["Hora", "Tipo", "Concepto", "Monto"]],
      body: filasMov,
      startY: y,
      theme: "plain",
      margin: { left: M, right: M },
      styles: { fontSize: 7, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 10, halign: "center" },
        2: { cellWidth: 24 },
        3: { cellWidth: 18, halign: "right" },
      },
    });
    y = (doc.lastAutoTable?.finalY || y) + 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("Total ingresos: " + montoTicket(totalIngresosMov), M, y);
    y += 5;
    doc.text("Total egresos: " + montoTicket(totalEgresosMov), M, y);
    y += 6;
  }
  y = nuevaPaginaSiCierre(doc, y);

  if (tieneApertura || (Array.isArray(movimientos) && movimientos.length > 0)) {
    const lineHeightCuadre = 4.5;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Cuadre efectivo", M, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const formulaStr = "Fondo inicial + Efectivo ventas + Ingresos - Egresos = Efectivo esperado";
    const formulaLines = doc.splitTextToSize(formulaStr, W - 2 * M);
    doc.text(formulaLines, M, y);
    y += formulaLines.length * lineHeightCuadre + 2;
    const calcStr = montoTicket(fondoInicial) + " + " + montoTicket(efectivoVentas) + " + " + montoTicket(totalIngresosMov) + " - " + montoTicket(totalEgresosMov) + " = " + montoTicket(efectivoEsperado);
    const calcLines = doc.splitTextToSize(calcStr, W - 2 * M);
    doc.text(calcLines, M, y);
    y += calcLines.length * lineHeightCuadre + 6;
    if (tieneConteo) {
      doc.text("Contado: " + montoTicket(efectivoContado), M, y);
      y += 5;
      const diffLabel = diferenciaEfectivo >= 0 ? "Sobra: " : "Falta: ";
      doc.text(diffLabel + montoTicket(Math.abs(diferenciaEfectivo)), M, y);
      y += 6;
    }
    y = nuevaPaginaSiCierre(doc, y);
  }

  if (desgloseEfectivo && typeof desgloseEfectivo === "object" && Object.keys(desgloseEfectivo).length > 0) {
    const orden = [20000, 10000, 5000, 2000, 1000, 500, 100, 50, 10];
    const filasDesglose = orden
      .filter((v) => desgloseEfectivo[v] > 0)
      .map((v) => [
        DENOM_LABELS[v] || `$${v}`,
        String(desgloseEfectivo[v]),
        fmtCLP(v * desgloseEfectivo[v]),
      ]);
    if (filasDesglose.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Desglose efectivo en caja", M, y);
      y += 5;
      doc.setFont("helvetica", "normal");
      autoTable(doc, {
        head: [["Denom.", "Cant.", "Subtotal"]],
        body: filasDesglose,
        startY: y,
        theme: "plain",
        margin: { left: M, right: M },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 28 },
          1: { cellWidth: 18, halign: "center" },
          2: { cellWidth: 24, halign: "right" },
        },
      });
      y = (doc.lastAutoTable?.finalY || y) + 4;
      const totalContado = orden.reduce((s, v) => s + (desgloseEfectivo[v] || 0) * v, 0);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text("Total efectivo contado: " + montoTicket(totalContado), M, y);
      y += 5;
    }
  }
  y = nuevaPaginaSiCierre(doc, y);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("TOTAL VENTAS NETAS:", M, y);
  doc.text(montoTicket(resumen.total_neto), W - M, y, { align: "right" });
  y += 6;
  doc.text("EFECTIVO ESPERADO EN CAJA:", M, y);
  doc.text(montoTicket(efectivoEsperado), W - M, y, { align: "right" });
  y += 10;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("— Fin del ticket de cierre —", W / 2, y, { align: "center" });

  abrirEImprimir(doc);
}

/**
 * Ticket de movimiento de caja (ingreso o egreso).
 * @param {{ nombreCajero: string, tipo: 'ingreso'|'egreso', monto: number, concepto?: string, fecha?: Date }} opts
 */
export function imprimirTicketMovimiento(opts) {
  if (shouldUseRawBT()) {
    _dispatchRawBT(buildMovimientoEscPos(opts));
    return;
  }
  const { nombreCajero = "", tipo = "ingreso", monto = 0, concepto = "", fecha = new Date() } = opts;
  const doc = new jsPDF({ unit: "mm", format: [W, 210] });

  let y = 10;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("La Sushitería", W / 2, y, { align: "center" });
  y += 6;
  doc.setFontSize(12);
  doc.text("MOVIMIENTO DE CAJA", W / 2, y, { align: "center" });
  y += 8;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(tipo === "ingreso" ? "INGRESO" : "EGRESO", W / 2, y, { align: "center" });
  y += 10;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Cajero:", M, y);
  doc.text(String(nombreCajero), M + 22, y);
  y += 6;
  doc.text("Fecha/hora:", M, y);
  doc.text(fechaHora(fecha), M + 22, y);
  y += 6;
  doc.text("Concepto:", M, y);
  doc.text(String(concepto || "—").slice(0, 35), M + 22, y);
  y += 10;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("MONTO:", M, y);
  const signo = tipo === "egreso" ? "- " : "+ ";
  const montoStr = "$" + (Number(monto) || 0).toLocaleString("es-CL", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  doc.text(signo + montoStr, W - M, y, { align: "right" });
  y += 10;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("— Fin del ticket de movimiento —", W / 2, y, { align: "center" });

  abrirEImprimir(doc);
}

function abrirEImprimir(doc) {
  doc.autoPrint({ type: "non" });
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  const ventana = window.open(url, "_blank", "noopener");
  if (ventana) {
    ventana.onload = () => {
      ventana.print();
      ventana.onafterprint = () => ventana.close();
    };
  } else {
    doc.save("ticket_caja.pdf");
    URL.revokeObjectURL(url);
  }
}
